import type { ReactNode } from 'react'
import type { SortState } from '@/lib/list'

import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { TableHead } from '@/components/ui/table'

interface SortableHeadProps<K extends string> {
    label: string
    sortKey: K
    sort: SortState<K>
    onSort: (key: K) => void
    className?: string
    align?: 'left' | 'right'
}

const SortableHead = <K extends string>({ label, sortKey, sort, onSort, className, align = 'left' }: SortableHeadProps<K>): ReactNode => {
    const active = sort.key === sortKey
    const Icon = !active ? ArrowUpDown : sort.direction === 'asc' ? ArrowUp : ArrowDown

    return (
        <TableHead
            className={cn(align === 'right' && 'text-right', className)}
            aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
            <button
                type='button'
                onClick={() => onSort(sortKey)}
                className={cn(
                    '-mx-1.5 inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                    active && 'text-foreground',
                    align === 'right' && 'flex-row-reverse'
                )}
            >
                {label}
                <Icon className={cn('size-3.5', !active && 'opacity-50')} aria-hidden='true' />
            </button>
        </TableHead>
    )
}

export default SortableHead
